import React from 'react';
import { motion } from 'framer-motion';
import { useProcessing } from '../../contexts/ProcessingContext';
import DocumentProcessor from './DocumentProcessor';
import LockedBrandProcessor from './LockedBrandProcessor';
import ProcessingResults from './ProcessingResults';

const steps = [
  { id: 'upload', title: 'Upload Files', component: DocumentProcessor },
  { id: 'process', title: 'Apply Branding', component: LockedBrandProcessor },
  { id: 'results', title: 'Results', component: ProcessingResults }
];

export default function ProcessingFlow() {
  const { currentStep, uploadedFiles } = useProcessing();
  const currentStepIndex = steps.findIndex(step => step.id === currentStep);
  const CurrentComponent = steps[currentStepIndex]?.component || DocumentProcessor;
  
  // Pass the first uploaded file to the processing components
  const activeDocument = uploadedFiles.length > 0 ? uploadedFiles[0] : null;

  return (
    <div className="max-w-7xl mx-auto p-6">
      <div className="mb-8">
        <h1 className="text-3xl font-bold text-gray-900 mb-2">Document Processing</h1>
        <p className="text-gray-600">Upload and transform your documents with locked brand styling</p>
      </div>
      
      {/* Step Indicator */}
      <div className="mb-8">
        <div className="flex items-center justify-between">
          {steps.map((step, index) => (
            <div key={step.id} className="flex items-center">
              <motion.div
                className={`w-10 h-10 rounded-full flex items-center justify-center text-sm font-medium ${
                  index <= currentStepIndex 
                    ? 'bg-blue-600 text-white' 
                    : 'bg-gray-200 text-gray-600'
                }`}
                initial={{ scale: 0.8 }}
                animate={{ scale: index === currentStepIndex ? 1.1 : 1 }}
                transition={{ duration: 0.2 }}
              >
                {index + 1}
              </motion.div>
              <span className={`ml-2 text-sm font-medium ${
                index <= currentStepIndex ? 'text-gray-900' : 'text-gray-500'
              }`}>
                {step.title}
              </span>
              {index < steps.length - 1 && (
                <div className={`ml-4 w-16 h-0.5 ${
                  index < currentStepIndex ? 'bg-blue-600' : 'bg-gray-200'
                }`} />
              )}
            </div>
          ))}
        </div>
      </div>
      
      {/* Current Step Content */}
      <motion.div
        key={currentStep}
        initial={{ opacity: 0, x: 20 }}
        animate={{ opacity: 1, x: 0 }}
        exit={{ opacity: 0, x: -20 }}
        transition={{ duration: 0.3 }}
      >
        {currentStep === 'process' ? (
          <LockedBrandProcessor documentFile={activeDocument?.originalFile} />
        ) : (
          <CurrentComponent />
        )}
      </motion.div>
    </div>
  );
}